import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Button, Select, Upload } from "antd";
import { categoryList, priorityList } from "./constants";
import { UploadOutlined } from "@ant-design/icons";
import localStorageContent from "../../../utils/localstorage";
import {
  RcFile,
  UploadChangeParam,
  UploadFile,
} from "antd/es/upload/interface";

export interface CreateTicketFormValues {
  subject: string;
  category: string;
  priority: string;
  description: string;
  file_path?: string;
  deptId?: any;
}

interface CreateSupportTicketModalProps {
  visible: boolean;
  onCreate: (values: any) => void;
  onUpdate: (values: any, ticket_id: any) => void;
  onCancel: () => void;
  record: any;
}

const CreateSupportTicketModal: React.FC<CreateSupportTicketModalProps> = ({
  visible,
  onCreate,
  onCancel,
  record,
  onUpdate,
}) => {
  const [form] = Form.useForm();
  const [file, setFile] = useState<RcFile | null>(null);
  const localUserData = localStorageContent.getUserData();

  const handleRemove = () => {
    setFile(null);
  };

  const handleFileChange = (info: UploadChangeParam<UploadFile>) => {
    if (info.fileList.length > 0 && info.fileList[0].originFileObj) {
      setFile(info.fileList[0].originFileObj as RcFile);
    } else {
      setFile(null);
    }
  };

  const submitTicket = (callback: (formData: FormData) => void) => {
    form
      .validateFields()
      .then((values: CreateTicketFormValues) => {
        const formData = new FormData();
        formData.append("subject", values.subject);
        formData.append("category", values.category);
        formData.append("priority", values.priority);
        formData.append("description", values.description);
        formData.append("deptId", localUserData?.departmentId);

        if (file) {
          formData.append("file", file);
        }

        setFile(null);
        callback(formData);
        form.resetFields();
      })
      .catch((errorInfo) => {
        console.warn("Form validation failed:", errorInfo);
      });
  };

  const createTicket = () => submitTicket(onCreate);
  const updateTicket = () =>
    submitTicket((formData) => onUpdate(formData, record.ticket_id));

  useEffect(() => {
    if (Object.keys(record).length > 0) {
      form.setFieldsValue({
        subject: record.subject,
        category: record.category,
        priority: record.priority,
        description: record.description,
        file_path: record.file_path,
      });
      setFile(null); // File is not pre-filled
    } else {
      form.resetFields();
      setFile(null);
    }
  }, [record, form]);

  return (
    <Modal
      open={visible}
      title={`${
        Object.keys(record).length > 0 ? "Update" : "Create"
      } Support Ticket`}
      okText={`${Object.keys(record).length > 0 ? "Update" : "Create"}`}
      cancelText="Cancel"
      onCancel={onCancel}
      onOk={Object.keys(record).length > 0 ? updateTicket : createTicket}
    >
      <Form form={form} layout="vertical" name="create_ticket_form">
        <Form.Item
          name="subject"
          label="Subject"
          rules={[{ required: true, message: "Please enter the subject" }]}
        >
          <Input placeholder="Subject" />
        </Form.Item>

        <Form.Item
          name="category"
          label="Category"
          rules={[{ required: true, message: "Please select a category" }]}
        >
          <Select placeholder="Select Category">
            {categoryList.map((option) => (
              <Select.Option key={option.value} value={option.value}>
                {option.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="priority"
          label="Priority"
          rules={[{ required: true, message: "Please select a priority" }]}
        >
          <Select placeholder="Select Priority">
            {priorityList.map((option) => (
              <Select.Option key={option.value} value={option.value}>
                {option.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="description"
          label="Description"
          rules={[{ required: true, message: "Please enter the description" }]}
        >
          <Input.TextArea rows={4} />
        </Form.Item>

        <Form.Item name="file_path" label="Attachment">
          <Upload
            fileList={
              file
                ? [
                    {
                      uid: "1",
                      name: file.name,
                      status: "done",
                      originFileObj: file,
                    },
                  ]
                : []
            }
            onChange={handleFileChange}
            onRemove={handleRemove}
            beforeUpload={() => false}
            maxCount={1}
          >
            <Button icon={<UploadOutlined />}>Upload</Button>
          </Upload>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreateSupportTicketModal;
